package com.islamicmedia.site.components

import com.islamicmedia.site.util.Utm
import com.islamicmedia.shared.domain.model.concernDisplayName
import androidx.compose.runtime.*
import com.islamicmedia.shared.domain.model.AnalysisResult
import com.islamicmedia.site.theme.*
import kotlinx.browser.document
import kotlinx.serialization.decodeFromString
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.buildJsonObject
import kotlinx.serialization.json.put
import kotlinx.serialization.json.putJsonObject
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

/**
 * Public, SEO-facing media review page ("Is <title> safe for Muslim kids?").
 *
 * Renders a REAL cached analysis that is BAKED IN at build time ([STATIC_REVIEWS], generated from
 * PROD by scripts/gen_isitsafe.py) through the live app's results UI — [CombinedAnalysisResultContent]
 * (verdict block, expandable concern cards with Islamic / Developmental tabs, Qur'an + hadith boxes,
 * positive aspects) — inside a mobile-style card: cover-art hero fading into a verdict-tinted sheet.
 *
 * The data is STATIC (not runtime-fetched) on purpose: Kobweb's static export is a headless-browser
 * snapshot that only captures content present in the initial synchronous render, so baking it in is
 * what puts the verdict + per-page <title>/<meta>/JSON-LD into the crawlable HTML. Refresh a verdict
 * by re-running the generator + redeploying.
 *
 * NOTE: stored Arabic in legacy result_json predates server-side Quran verification; correcting it
 * via POST /api/quran/resolve (as the app's applyVerifiedQuran does) is still a TODO before promotion.
 */

private val reviewJson = Json { ignoreUnknownKeys = true; isLenient = true }

private class ReviewData(
    val title: String,
    val mediaType: String,
    val year: String,
    val coverUrl: String?,
    val result: AnalysisResult,
)

@Composable
fun PublicReviewPage(
    /** Slug into [STATIC_REVIEWS] (e.g. "moana"). The analysis is baked in at build time. */
    slug: String,
    /** Public-facing intro shown above the analysis — orients cold SEO visitors who don't know the app. */
    blurb: String,
) {
    val staticReview = STATIC_REVIEWS[slug]
    if (staticReview == null) {
        PageChrome { StatusBlock("No analysis found for this title yet.") }
        return
    }
    // Decode synchronously during composition so the content is in the initial render — the static
    // export snapshot then captures it (and the head below) into the crawlable HTML.
    val data = remember(slug) {
        val result = reviewJson.decodeFromString<AnalysisResult>(staticReview.resultJson)
        ReviewData(staticReview.title, staticReview.mediaType, staticReview.year, staticReview.coverUrl.ifBlank { null }, result)
    }

    // Per-page <head>: unique title, description, Open Graph, and JSON-LD Review schema (the latter
    // drives ⭐ rating snippets in Google). SideEffect runs right after first composition, before the
    // export snapshot settles, so the head is captured.
    SideEffect { applySeoHead(data) }

    PageChrome { Loaded(data, blurb, slug) }
}

@Composable
private fun Loaded(d: ReviewData, blurb: String, slug: String) {
    val onArt = !d.coverUrl.isNullOrBlank()

    // SEO headline (the search query, visible). Dark text — the gradient is near-white here.
    H1(attrs = {
        style {
            property("margin", "16px 0 8px")
            property("font-family", TitleFontFamily)
            fontSize(26.px)
            fontWeight(800)
            property("line-height", "1.25")
            property("color", "#142E2E")
        }
    }) { Text("Is ${d.title} safe for Muslim kids?") }

    // Intro blurb — below the title, outside the card.
    Div(attrs = {
        style {
            property("margin", "0 0 20px")
            fontSize(15.px)
            lineHeight(1.6.em)
            color(Gray600)
        }
    }) { Text(blurb) }

    // Result card — cover hero at top only, verdict-tinted sheet below (mobile layout).
    ResultCard(title = d.title, year = d.year, mediaType = d.mediaType, coverUrl = d.coverUrl, onArt = onArt, score = d.result.score) {
        CombinedAnalysisResultContent(result = d.result)
    }

    // Crawlable full-text mirror of the concern depth (descriptions, Islamic perspectives, verified
    // Arabic, positives) — the same content users reveal by expanding a concern. Visually hidden, so
    // the page looks identical, but it's in the static HTML for search engines and screen readers.
    CrawlableConcernText(d)

    // Call to action. Tagged per-slug: these SEO pages are a real acquisition channel and the
    // whole point is knowing WHICH title brought someone in, not just that /isitsafe did.
    A(href = Utm.tag("/", source = "isitsafe", medium = "seo", content = slug), attrs = {
        style {
            display(DisplayStyle.Block)
            property("text-decoration", "none")
            property("text-align", "center")
            backgroundColor(Color.white)
            color(Primary)
            fontSize(16.px); fontWeight(700)
            padding(16.px)
            marginTop(24.px)
            borderRadius(RadiusFull.unsafeCast<CSSNumeric>())
            property("box-shadow", "0 8px 20px rgba(0, 0, 0, 0.18)")
        }
    }) { Text("Check any title for your family") }

    Div(attrs = {
        style {
            fontSize(12.px); property("color", "rgba(255,255,255,0.85)")
            property("text-align", "center"); property("margin", "12px 0 0")
        }
    }) { Text("Instant · grounded in the Qur'an · for Muslim families") }

    Div(attrs = { style { marginTop(20.px) } }) { DisclaimerFooter() }
}

/**
 * Visually-hidden, screen-reader- and crawler-accessible full text of every concern's detail — the
 * same content a user sees when they expand a concern card. Renders into the initial DOM (so Kobweb's
 * static export captures it) using the standard sr-only clip technique, so the visible page is
 * unchanged. Gives each page its full depth of unique, keyword-rich text + the verified Arabic.
 */
@Composable
private fun CrawlableConcernText(d: ReviewData) {
    Div(attrs = {
        style {
            property("position", "absolute")
            property("width", "1px"); property("height", "1px")
            property("padding", "0"); property("margin", "-1px")
            property("overflow", "hidden")
            property("clip", "rect(0, 0, 0, 0)")
            property("white-space", "normal")
            property("border", "0")
        }
    }) {
        d.result.recommendation.takeIf { it.isNotBlank() }?.let { P { Text(it) } }
        d.result.ageGuidance?.takeIf { it.isNotBlank() }?.let { P { Text("Age guidance: $it") } }

        val sorted = d.result.concerns.sortedBy {
            when (it.severity.lowercase()) { "high" -> 0; "medium" -> 1; else -> 2 }
        }
        sorted.forEach { c ->
            H2 { Text("${concernDisplayName(c.category)}: ${c.severity} concern") }
            if (c.description.isNotBlank()) P { Text(c.description) }
            c.islamicPerspective?.takeIf { it.isNotBlank() }?.let {
                H3 { Text("Islamic perspective") }; P { Text(it) }
            }
            c.quranReference?.let { q ->
                P { Text("${q.english} (${q.reference})") }
                P(attrs = { attr("dir", "rtl"); attr("lang", "ar") }) { Text(q.arabic) }
            }
            c.hadithReference?.let { hd ->
                P { Text("${hd.english} (${hd.reference})") }
                P(attrs = { attr("dir", "rtl"); attr("lang", "ar") }) { Text(hd.arabic) }
            }
            c.developmentalPerspective?.takeIf { it.isNotBlank() }?.let {
                H3 { Text("Child development perspective") }; P { Text(it) }
            }
        }
        if (d.result.positiveAspects.isNotEmpty()) {
            H2 { Text("Positive aspects of ${d.title}") }
            Ul { d.result.positiveAspects.forEach { Li { Text(it) } } }
        }
    }
}

/** Page background chrome + centered content column + top bar, shared by every state. */
@Composable
private fun PageChrome(content: @Composable () -> Unit) {
    Div(attrs = {
        style {
            property("min-height", "100vh")
            property("background", GradientHeader)
            property("font-family", "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif")
            property("box-sizing", "border-box")
            padding(20.px, 16.px, 40.px)
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            alignItems(AlignItems.Center)
        }
    }) {
        Div(attrs = { style { width(100.percent); property("max-width", "680px") } }) {
            AppHero()
            content()
        }
    }
    InjectStyles(
        "public-review-styles",
        "@keyframes pr-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }"
    )
}

@Composable
private fun StatusBlock(message: String, spinner: Boolean = false) {
    Div(attrs = {
        style {
            property("min-height", "40vh")
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            alignItems(AlignItems.Center)
            justifyContent(JustifyContent.Center)
            gap(16.px)
            property("text-align", "center")
            marginTop(40.px)
        }
    }) {
        if (spinner) {
            Div(attrs = {
                style {
                    width(40.px); height(40.px)
                    property("border", "3px solid rgba(255,255,255,0.4)")
                    property("border-top-color", "#FFFFFF")
                    borderRadius(50.percent)
                    property("animation", "pr-spin 0.8s linear infinite")
                }
            }) {}
        }
        Div(attrs = { style { fontSize(15.px); fontWeight(600); property("color", "#FFFFFF") } }) { Text(message) }
    }
}

/** The app's hero header — centered logo + tagline, matching the main page. Links home. */
@Composable
fun AppHero() {
    A(href = "/", attrs = {
        style {
            property("text-decoration", "none")
            width(100.percent)
            padding(8.px, 20.px, 0.px)
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            alignItems(AlignItems.Center)
            property("text-align", "center")
        }
    }) {
        Img(src = "/images/logo.png", attrs = {
            attr("alt", "Fitrah Filter")
            style {
                width(150.px)
                height(150.px)
                property("object-fit", "contain")
                property("filter", "drop-shadow(0 4px 12px rgba(0,0,0,0.2))")
            }
        })
        P(attrs = {
            style {
                color(Color("#5a6a6a"))
                fontSize(13.px)
                margin(6.px, 0.px, 0.px)
                fontWeight(600)
                property("letter-spacing", "0.5px")
            }
        }) { Text("Protecting Fitrah. Empowering Parents.") }
    }
}

/**
 * Mobile-style result card: a fixed-height cover hero at the TOP, fading via a gradient foot into a
 * verdict-tinted opaque sheet (green / amber / red by score) that carries the title and all content.
 * Title sits on the sheet — never on the raw art — so it stays legible.
 */
@Composable
private fun ResultCard(
    title: String,
    year: String,
    mediaType: String,
    coverUrl: String?,
    onArt: Boolean,
    score: Int,
    content: @Composable () -> Unit,
) {
    val (sheetRgb, borderHex) = when {
        score >= 7 -> "240, 253, 244" to "#22C55E"
        score >= 4 -> "255, 251, 235" to "#F59E0B"
        else -> "254, 242, 242" to "#EF4444"
    }

    Div(attrs = {
        style {
            property("background", "rgb($sheetRgb)")
            borderRadius(RadiusXLarge.unsafeCast<CSSNumeric>())
            property("box-shadow", NeuBoxShadow)
            property("border", "2px solid $borderHex")
            property("overflow", "hidden")
        }
    }) {
        // Cover hero banner — top only.
        Div(attrs = {
            style {
                property("position", "relative")
                height(200.px)
                property("overflow", "hidden")
            }
        }) {
            Div(attrs = {
                style {
                    property("position", "absolute")
                    property("inset", "0")
                    if (onArt && coverUrl != null) {
                        property("background-image", "url($coverUrl)")
                        property("background-size", "cover")
                        property("background-position", "center 30%")
                    } else {
                        property("background", GradientPrimary)
                    }
                }
            }) {}
            Div(attrs = {
                style {
                    property("position", "absolute")
                    property("left", "0"); property("right", "0"); property("bottom", "0")
                    height(96.px)
                    property("background", "linear-gradient(rgba($sheetRgb, 0) 0%, rgb($sheetRgb) 100%)")
                }
            }) {}
        }

        // Content sheet
        Div(attrs = { style { padding(4.px, 28.px, 32.px) } }) {
            Div(attrs = {
                style {
                    property("text-align", "center")
                    padding(0.px, 12.px)
                }
            }) {
                H2(attrs = {
                    style {
                        margin(0.px)
                        property("font-family", TitleFontFamily)
                        fontSize(24.px)
                        fontWeight(700)
                        property("line-height", "1.25")
                        property("color", "#171717")
                    }
                }) { Text(title) }
                Div(attrs = {
                    style {
                        marginTop(4.px)
                        fontSize(14.px)
                        fontWeight(500)
                        color(Gray500)
                        property("text-transform", "capitalize")
                    }
                }) { Text(if (year.isNotBlank()) "$mediaType · $year" else mediaType) }
            }

            content()
        }
    }
}

/** Map our humanised media type to a schema.org type for JSON-LD itemReviewed. */
private fun ldType(mediaType: String): String = when (mediaType) {
    "Movie" -> "Movie"
    "TV show" -> "TVSeries"
    "Game" -> "VideoGame"
    "Book" -> "Book"
    else -> "CreativeWork"
}

/** Set the page's unique <title>, meta description, Open Graph, and JSON-LD Review schema. */
private fun applySeoHead(d: ReviewData) {
    val q = "Is ${d.title} safe for Muslim kids?"
    val desc = ("${d.title} (${d.year}) reviewed against Islamic values, verdict ${d.result.score}/10. " +
        d.result.recommendation).take(300)
    document.title = "$q | Islamic review | Fitrah Filter"
    upsertMeta("name", "description", desc)
    upsertMeta("property", "og:title", q)
    upsertMeta("property", "og:description", desc)
    upsertMeta("property", "og:type", "article")
    if (!d.coverUrl.isNullOrBlank()) upsertMeta("property", "og:image", d.coverUrl)

    val ld = buildJsonObject {
        put("@context", "https://schema.org")
        put("@type", "Review")
        putJsonObject("itemReviewed") {
            put("@type", ldType(d.mediaType))
            put("name", d.title)
        }
        putJsonObject("reviewRating") {
            put("@type", "Rating")
            put("ratingValue", d.result.score)
            put("bestRating", 10)
            put("worstRating", 1)
        }
        putJsonObject("author") {
            put("@type", "Organization")
            put("name", "Fitrah Filter")
        }
        put("name", q)
        put("reviewBody", d.result.recommendation)
    }.toString()

    val script = document.getElementById("ld-review") ?: document.createElement("script").also {
        it.setAttribute("type", "application/ld+json")
        it.setAttribute("id", "ld-review")
        document.head?.appendChild(it)
    }
    script.textContent = ld
}

/** Create-or-update a <meta> tag keyed by an attribute (name= or property=). */
private fun upsertMeta(keyAttr: String, key: String, content: String) {
    val el = document.querySelector("meta[$keyAttr=\"$key\"]") ?: document.createElement("meta").also {
        it.setAttribute(keyAttr, key)
        document.head?.appendChild(it)
    }
    el.setAttribute("content", content)
}
